<script setup lang="ts">
import { useShareActions } from '../../composables/useShareActions';

const { unitName } = defineProps<{
  unitName?: string;
}>();

const { 
  showShareMenu,
  shareOnFacebook,
  copyUrlToClipboard,
  toggleShareMenu,
  shareByEmail
} = useShareActions(unitName);
</script>

<template>
  <div class="shareContainer">
    <button class="shareButton" @click="toggleShareMenu">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
        <circle cx="18" cy="5" r="3"/>
        <circle cx="6" cy="12" r="3"/>
        <circle cx="18" cy="19" r="3"/>
        <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/>
        <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/>
      </svg>
    </button>
    <div :class="['shareMenu', { visible: showShareMenu }]">
      <button @click="shareOnFacebook" class="shareMenuItem">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
          <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
        </svg>
      </button>
      <button @click="shareByEmail" class="shareMenuItem">
        <svg width="24" height="24" viewBox="0 0 16 12" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.5 0H14.5C15.3125 0 16 0.6875 16 1.5C16 2 15.75 2.4375 15.375 2.71875L8.59375 7.8125C8.21875 8.09375 7.75 8.09375 7.375 7.8125L0.59375 2.71875C0.21875 2.4375 0 2 0 1.5C0 0.6875 0.65625 0 1.5 0ZM0 3.5L6.78125 8.625C7.5 9.15625 8.46875 9.15625 9.1875 8.625L16 3.5V10C16 11.125 15.0938 12 14 12H2C0.875 12 0 11.125 0 10V3.5Z" fill="currentColor"></path>
        </svg>
      </button>
      <button @click="copyUrlToClipboard" class="shareMenuItem">
        <svg width="24" height="24" viewBox="0 0 20 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18.0938 8.375L14.5938 11.875C12.8438 13.6562 9.96875 13.6562 8.21875 11.875C6.65625 10.3125 6.4375 7.875 7.71875 6.0625L7.75 6.03125C8.09375 5.5625 8.71875 5.46875 9.15625 5.78125C9.59375 6.09375 9.71875 6.71875 9.375 7.1875L9.34375 7.21875C8.625 8.21875 8.75 9.59375 9.625 10.4688C10.5938 11.4688 12.1875 11.4688 13.1875 10.4688L16.6875 6.96875C17.6875 5.96875 17.6875 4.375 16.6875 3.40625C15.8125 2.53125 14.4375 2.40625 13.4375 3.125L13.4062 3.15625C12.9375 3.5 12.3125 3.375 12 2.9375C11.6875 2.5 11.7812 1.875 12.2188 1.53125L12.2812 1.5C14.0938 0.21875 16.5312 0.4375 18.0938 2C19.875 3.75 19.875 6.625 18.0938 8.375ZM1.875 7.65625L5.375 4.125C7.15625 2.375 10 2.375 11.7812 4.125C13.3438 5.6875 13.5312 8.15625 12.25 9.96875L12.2188 10C11.9062 10.4688 11.25 10.5625 10.8125 10.25C10.375 9.9375 10.25 9.3125 10.5938 8.84375L10.625 8.8125C11.3438 7.78125 11.2188 6.4375 10.3438 5.5625C9.375 4.5625 7.78125 4.5625 6.78125 5.5625L3.28125 9.0625C2.3125 10.0312 2.3125 11.625 3.28125 12.625C4.15625 13.5 5.53125 13.625 6.53125 12.9062L6.5625 12.875C7.03125 12.5312 7.65625 12.6562 7.96875 13.0938C8.28125 13.5312 8.1875 14.1562 7.75 14.5L7.6875 14.5312C5.90625 15.8125 3.4375 15.5938 1.875 14.0312C0.09375 12.2812 0.09375 9.40625 1.875 7.65625Z" fill="currentColor"></path>
        </svg>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.shareContainer {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 4.8rem;  
  height: 4.8rem;
  border: 2px solid var(--gray-200);
  border-radius: 99px;
  margin: 0;

  &:hover {
    background-color: #ffffff;
  }
}

.shareButton {
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s;
  color: var(--gray-950);
  height: 1.8rem;
  width: 1.8rem;

  svg {
    height: 100%;
    width: 100%;
    display: block;
  }
}

.shareMenu {
  opacity: 0;
  pointer-events: none;
  position: absolute;
  right: 0;
  top: calc(100% + 1rem);
  display: flex;
  flex-flow: column nowrap;
  gap: 1rem;
  z-index: 100;

  &.visible{
    opacity: 1;
    pointer-events: visible;
  }

  .shareMenuItem {
    border-radius: 9999px;
    background: #E5E5E5;
    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.10), 0 1px 1px 0 rgba(0, 0, 0, 0.06);
    display: flex;
    justify-content: center;
    align-items: center;
    color: #313241;
    width: 4.8rem;
    height: 4.8rem;
    border: 2px solid var(--gray-200);
    background-color: #f5f5f5;

    &:hover {
      background-color: #ffffff;
    }

    svg {
      width: 2.0rem;
      height: 2.0rem;
      flex-shrink: 0;
    }
  }
}
</style>